import { useState, useRef, useCallback } from 'react';

export interface UseHoverCardOptions {
  /**
   * Delay in milliseconds before showing the hover card
   * @default 150
   */
  showDelay?: number;

  /**
   * Delay in milliseconds before hiding the hover card
   * @default 150
   */
  hideDelay?: number;

  /**
   * Optional callback to execute when card opens
   */
  onOpen?: () => void;

  /**
   * Optional callback to execute when card closes
   */
  onClose?: () => void;
}

export interface UseHoverCardReturn {
  /** Whether the hover card is currently visible */
  isVisible: boolean;

  /** Handler for mouse enter on the trigger element */
  onTriggerMouseEnter: () => void;

  /** Handler for mouse leave on the trigger element */
  onTriggerMouseLeave: () => void;

  /** Handler for mouse enter on the hover card itself */
  onCardMouseEnter: () => void;

  /** Handler for mouse leave on the hover card itself */
  onCardMouseLeave: () => void;

  /** Function to manually close the hover card */
  close: () => void;
}

/**
 * Hook to manage hover card visibility with delay timing
 *
 * @example
 * ```tsx
 * const hoverCard = useHoverCard({ showDelay: 200 });
 *
 * return (
 *   <>
 *     <span
 *       onMouseEnter={hoverCard.onTriggerMouseEnter}
 *       onMouseLeave={hoverCard.onTriggerMouseLeave}
 *     >
 *       Hover me
 *     </span>
 *
 *     {hoverCard.isVisible && (
 *       <HoverCard
 *         onMouseEnter={hoverCard.onCardMouseEnter}
 *         onMouseLeave={hoverCard.onCardMouseLeave}
 *         onClose={hoverCard.close}
 *       />
 *     )}
 *   </>
 * );
 * ```
 */
export const useHoverCard = (options: UseHoverCardOptions = {}): UseHoverCardReturn => {
  const {
    showDelay = 150,
    hideDelay = 150,
    onOpen,
    onClose,
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear all timeouts
  const clearTimeouts = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
  }, []);

  // Handle mouse enter on trigger element
  const onTriggerMouseEnter = useCallback(() => {
    clearTimeouts();

    // Set delay before showing card
    showTimeoutRef.current = setTimeout(() => {
      setIsVisible(true);
      onOpen?.();
    }, showDelay);
  }, [clearTimeouts, showDelay, onOpen]);

  // Handle mouse leave on trigger element
  const onTriggerMouseLeave = useCallback(() => {
    clearTimeouts();

    // Delay hiding to allow user to move to card
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, hideDelay);
  }, [clearTimeouts, hideDelay, onClose]);

  // Handle mouse enter on card (keep it visible)
  const onCardMouseEnter = useCallback(() => {
    clearTimeouts();
  }, [clearTimeouts]);

  // Handle mouse leave on card
  const onCardMouseLeave = useCallback(() => {
    clearTimeouts();

    // Hide after delay
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, hideDelay);
  }, [clearTimeouts, hideDelay, onClose]);

  // Manual close function
  const close = useCallback(() => {
    clearTimeouts();
    setIsVisible(false);
    onClose?.();
  }, [clearTimeouts, onClose]);

  return {
    isVisible,
    onTriggerMouseEnter,
    onTriggerMouseLeave,
    onCardMouseEnter,
    onCardMouseLeave,
    close,
  };
};
